import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Bars3Icon,
  XMarkIcon,
  Cog6ToothIcon,
  ArrowRightStartOnRectangleIcon,
  DocumentArrowUpIcon,
  HomeIcon,
  FolderOpenIcon,
  BuildingOffice2Icon,
  LockClosedIcon,
  ShareIcon,
  ClockIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { PanelLeftClose } from 'lucide-react';
import UploadIndicator from './UploadIndicator';
import { canSwitchWorkspaces } from '../racing/config';
import { useUser } from '../context/UserContext';
import amesLogo from '../assets/appLogoFinalLight.svg';
import acIcon from '../assets/aclogo.png';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const navigation = [
  { name: 'Home', href: '/home', icon: HomeIcon },
  { name: 'Projects', href: '/projects', icon: FolderOpenIcon },
  { name: 'Share Links', href: '/share-links', icon: ShareIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

export default function Layout({
  children,
  currentUser,
  currentCompany,
  onSignOut,
  currentPage = 'home',
  workspace = 'ames'
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isRacing = workspace === 'racing';
  const workspaceLogo = isRacing ? '/nascar-logo.png' : amesLogo;
  const workspaceLogoAlt = isRacing ? 'NASCAR' : 'Ames Connect';
  const homeHref = isRacing ? '/racing' : '/home';
  const uploadHref = isRacing ? '/racing/upload' : '/upload';
  const workspaceNavigation = isRacing ? [
    { name: 'Home', href: '/racing', icon: HomeIcon },
    { name: 'Projects', href: '/racing/projects', icon: FolderOpenIcon },
    { name: 'Settings', href: '/racing/settings', icon: Cog6ToothIcon },
    ...(canSwitchWorkspaces(currentCompany) ? [{ name: 'Ames workspace', href: '/home', icon: BuildingOffice2Icon }] : []),
  ] : [...navigation, ...(canSwitchWorkspaces(currentCompany) ? [{ name: 'Racing workspace', href: '/racing', icon: CalendarDaysIcon }] : [])];
  const isNoCompany = !currentCompany;
  const { subscription } = useUser();

  const isTrial = subscription?.is_trialing;
  const trialDaysLeft = subscription?.trial_days_remaining;
  const isUrgentTrial = isTrial && trialDaysLeft != null && trialDaysLeft <= 3;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('sidebarCollapsed');
        return saved !== null ? JSON.parse(saved) : false;
      }
    } catch (e) {}
    return false;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [tooltip, setTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const tooltipLeaveRef = useRef(null);

  const user = currentUser || { name: 'User', email: 'user@company.com' };
  const initials = user.name ? user.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : 'U';

  const handleNavClick = (href) => {
    if (isRacing && href.startsWith('/settings')) href = `/racing${href}`;
    if (isNoCompany && !['/profile', '/settings'].includes(href)) return;
    navigate(href);
    setSidebarOpen(false);
  };

  const toggleSidebarCollapse = () => {
    const next = !sidebarCollapsed;
    setSidebarCollapsed(next);
    try { localStorage.setItem('sidebarCollapsed', JSON.stringify(next)); } catch (e) {}
    setTooltip(null);
  };

  const handleTooltipEnter = (e, content) => {
    clearTimeout(tooltipLeaveRef.current);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPos({ x: rect.right + 8, y: rect.top + rect.height / 2 });
    setTooltip(content);
  };

  const handleTooltipLeave = () => {
    tooltipLeaveRef.current = setTimeout(() => setTooltip(null), 100);
  };

  useEffect(() => {
    const onScroll = () => setTooltip(null);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, []);

  const getIsCurrent = (item) => {
    return currentPage === item.name ||
      (item.name === 'Share Links' && location.pathname === '/share-links') ||
      (item.name === 'Settings' && location.pathname === '/settings') ||
      (item.name === 'Projects' && (location.pathname.startsWith('/projects') || location.pathname.startsWith('/session')));
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 text-gray-900" onClick={() => sidebarOpen && setSidebarOpen(false)}>
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 z-40 flex h-12 items-center border-b border-gray-200 bg-white md:hidden">
        <button onClick={(e) => { e.stopPropagation(); setSidebarOpen(!sidebarOpen); }} className="ml-3 p-2 text-gray-400 hover:text-gray-600 transition-colors">
          {sidebarOpen ? <XMarkIcon className="h-5 w-5" /> : <Bars3Icon className="h-5 w-5" />}
        </button>
        <Link to={homeHref} className="ml-2">
          <img src={workspaceLogo} alt={workspaceLogoAlt} className={isRacing ? "h-5 w-28 object-contain" : "h-4"} />
        </Link>
      </div>

      {/* Sidebar */}
      <div
        className={classNames(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]',
          sidebarOpen ? 'translate-x-0 w-52' : '-translate-x-full w-52',
          'md:relative md:translate-x-0',
          sidebarCollapsed ? 'md:w-16' : 'md:w-52'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — crossfade logo/icon */}
        <div className="h-14 border-b border-gray-200 relative shrink-0">
          <div className="hidden md:block h-full">
            {/* Expanded */}
            <div
              className="absolute inset-0 flex items-center justify-between px-4"
              style={{ opacity: sidebarCollapsed ? 0 : 1, pointerEvents: sidebarCollapsed ? 'none' : 'auto', transition: 'opacity 250ms ease' }}
            >
              <Link to={homeHref}><img src={workspaceLogo} alt={workspaceLogoAlt} className={isRacing ? "h-5 w-28 object-contain" : "h-4"} /></Link>
              <button onClick={toggleSidebarCollapse} className="h-7 w-7 flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                <PanelLeftClose className="h-3.5 w-3.5" />
              </button>
            </div>
            {/* Collapsed */}
            <div
              className="absolute inset-0 flex items-center justify-center"
              style={{ opacity: sidebarCollapsed ? 1 : 0, pointerEvents: sidebarCollapsed ? 'auto' : 'none', transition: 'opacity 250ms ease' }}
            >
              <button onClick={toggleSidebarCollapse} className="h-9 w-9 flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors">
                <img src={isRacing ? workspaceLogo : acIcon} alt={isRacing ? workspaceLogoAlt : "AC"} className="h-7 w-7 rounded object-contain" />
              </button>
            </div>
          </div>
          {/* Mobile */}
          <div className="md:hidden flex items-center h-full px-4">
            <img src={workspaceLogo} alt={workspaceLogoAlt} className={isRacing ? "h-5 w-28 object-contain" : "h-4"} />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2.5 py-3 flex flex-col">
          <ul className="space-y-0.5 flex-1">
            {workspaceNavigation.map((item) => {
              const isCurrent = getIsCurrent(item);
              const isDisabled = isNoCompany && !['/settings'].includes(item.href);
              const Icon = isDisabled ? LockClosedIcon : item.icon;

              return (
                <li key={item.name} className="relative">
                  <button
                    onClick={() => !isDisabled && handleNavClick(item.href)}
                    onMouseEnter={(e) => {
                      if (isDisabled) handleTooltipEnter(e, 'Join a team to access');
                      else if (sidebarCollapsed) handleTooltipEnter(e, item.name);
                    }}
                    onMouseLeave={handleTooltipLeave}
                    className={classNames(
                      'group flex w-full items-center rounded-md text-sm font-medium relative',
                      isDisabled
                        ? 'text-gray-400 cursor-not-allowed opacity-50'
                        : isCurrent
                          ? 'bg-red-50 text-red-900 border border-red-200/60'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-transparent',
                    )}
                    style={{
                      padding: '0.4rem 0.75rem',
                      transition: 'background-color 200ms ease, color 200ms ease, box-shadow 200ms ease',
                      ...(isCurrent && !isDisabled && {
                        boxShadow: '0 1px 3px rgba(220, 38, 38, 0.08)',
                      }),
                    }}
                  >
                    {/* Active indicator */}
                    {isCurrent && !isDisabled && (
                      <div className="absolute -left-2.5 top-1/2 -translate-y-1/2 w-1 h-5 bg-red-600 rounded-r-full" />
                    )}
                    <Icon
                      className={classNames('h-4 w-4 shrink-0', isCurrent ? 'text-red-600' : 'text-gray-400')}
                      style={{
                        marginRight: sidebarCollapsed ? 0 : '0.65rem',
                        transition: 'margin 300ms cubic-bezier(0.4, 0, 0.2, 1), color 200ms ease',
                      }}
                    />
                    <span
                      className="truncate overflow-hidden"
                      style={{
                        maxWidth: sidebarCollapsed ? 0 : '10rem',
                        opacity: sidebarCollapsed ? 0 : 1,
                        transition: 'max-width 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease',
                      }}
                    >
                      {item.name}
                    </span>
                    {isDisabled && !sidebarCollapsed && (
                      <LockClosedIcon className="h-3.5 w-3.5 text-gray-400 ml-auto" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Join Team */}
          {isNoCompany && (
            <div className="mt-auto pt-2">
              <button
                onClick={() => handleNavClick('/settings')}
                onMouseEnter={(e) => sidebarCollapsed && handleTooltipEnter(e, 'Join Team')}
                onMouseLeave={handleTooltipLeave}
                className={classNames(
                  'flex w-full items-center rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors',
                  sidebarCollapsed ? 'justify-center' : 'justify-start',
                )}
                style={{ padding: '0.4rem 0.75rem' }}
              >
                <BuildingOffice2Icon
                  className="h-4 w-4 shrink-0"
                  style={{ marginRight: sidebarCollapsed ? 0 : '0.65rem', transition: 'margin 300ms cubic-bezier(0.4, 0, 0.2, 1)' }}
                />
                <span
                  className="truncate overflow-hidden"
                  style={{ maxWidth: sidebarCollapsed ? 0 : '10rem', opacity: sidebarCollapsed ? 0 : 1, transition: 'max-width 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease' }}
                >
                  Join Team
                </span>
              </button>
            </div>
          )}

          {/* Upload */}
          <div className="mt-2 pt-2">
            <button
              onClick={() => !isNoCompany && handleNavClick(uploadHref)}
              onMouseEnter={(e) => {
                if (isNoCompany) handleTooltipEnter(e, 'Join a team to upload');
                else if (sidebarCollapsed) handleTooltipEnter(e, 'Upload');
              }}
              onMouseLeave={handleTooltipLeave}
              className={classNames(
                'flex w-full items-center rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors',
                sidebarCollapsed ? 'justify-center' : 'justify-start',
                currentPage === 'Upload' ? 'ring-1 ring-red-400 ring-offset-1' : '',
                isNoCompany ? 'cursor-not-allowed opacity-50 hover:bg-red-600' : ''
              )}
              style={{ padding: '0.4rem 0.75rem' }}
            >
              <DocumentArrowUpIcon
                className="h-4 w-4 shrink-0"
                style={{ marginRight: sidebarCollapsed ? 0 : '0.65rem', transition: 'margin 300ms cubic-bezier(0.4, 0, 0.2, 1)' }}
              />
              <span
                className="truncate overflow-hidden"
                style={{ maxWidth: sidebarCollapsed ? 0 : '10rem', opacity: sidebarCollapsed ? 0 : 1, transition: 'max-width 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease' }}
              >
                Upload
              </span>
            </button>
          </div>
        </nav>

        {/* Footer — Trial card + User profile + sign out */}
        <div className="border-t border-gray-200 p-2 space-y-1">
          {isTrial && (
            sidebarCollapsed ? (
              <div className="flex justify-center mb-1">
                <button
                  onClick={() => handleNavClick('/settings?tab=subscription')}
                  onMouseEnter={(e) => handleTooltipEnter(e, `Trial · ${trialDaysLeft}d left`)}
                  onMouseLeave={handleTooltipLeave}
                  className={classNames(
                    'h-9 w-9 rounded-md border flex items-center justify-center transition-colors',
                    isUrgentTrial
                      ? 'bg-red-50 border-red-200 text-red-600 hover:bg-red-100'
                      : 'bg-amber-50 border-amber-200 text-amber-600 hover:bg-amber-100'
                  )}
                >
                  <ClockIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div
                className={classNames(
                  'rounded-md border p-2.5 mb-1',
                  isUrgentTrial
                    ? 'bg-red-50 border-red-200'
                    : 'bg-amber-50 border-amber-200'
                )}
              >
                <div className="flex items-center gap-1.5 mb-1.5">
                  <ClockIcon
                    className={classNames(
                      'h-3.5 w-3.5 shrink-0',
                      isUrgentTrial ? 'text-red-600' : 'text-amber-600'
                    )}
                  />
                  <p
                    className={classNames(
                      'text-xs font-semibold',
                      isUrgentTrial ? 'text-red-900' : 'text-amber-900'
                    )}
                  >
                    Trial · {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} left
                  </p>
                </div>
                <button
                  onClick={() => handleNavClick('/settings?tab=subscription')}
                  className={classNames(
                    'w-full text-xs font-medium rounded px-2 py-1 transition-colors',
                    isUrgentTrial
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-amber-600 text-white hover:bg-amber-700'
                  )}
                >
                  Upgrade
                </button>
              </div>
            )
          )}
          {sidebarCollapsed ? (
            <div className="flex flex-col items-center gap-1.5">
              <button
                onMouseEnter={(e) => handleTooltipEnter(e, user.name)}
                onMouseLeave={handleTooltipLeave}
                className="h-9 w-9 rounded-md bg-gray-100 border border-gray-200 text-xs font-semibold text-gray-700 flex items-center justify-center hover:bg-gray-200 transition-colors"
              >
                {initials}
              </button>
              <button
                onClick={onSignOut}
                onMouseEnter={(e) => handleTooltipEnter(e, 'Sign out')}
                onMouseLeave={handleTooltipLeave}
                className="h-9 w-9 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 flex items-center justify-center transition-colors"
              >
                <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-gray-50 border border-gray-100">
                <div className="h-7 w-7 rounded-md bg-gray-200 text-[10px] font-semibold text-gray-700 flex items-center justify-center shrink-0">
                  {initials}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-900 truncate">{user.name}</p>
                  <p className="text-[10px] text-gray-400 truncate">{user.email}</p>
                </div>
              </div>
              <button
                onClick={onSignOut}
                className="flex w-full items-center rounded-md px-3 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
              >
                <ArrowRightStartOnRectangleIcon className="h-4 w-4 mr-2 shrink-0" />
                Sign out
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {children}
        </div>
      </main>

      {/* Global Upload Indicator */}
      <UploadIndicator />

      {/* Tooltip */}
      {tooltip && ReactDOM.createPortal(
        <div
          className="fixed pointer-events-none z-[9999] bg-gray-800 text-white px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap shadow-lg border border-gray-700/50"
          style={{
            top: `${tooltipPos.y}px`,
            left: `${tooltipPos.x}px`,
            transform: 'translateY(-50%)',
          }}
        >
          {tooltip}
        </div>,
        document.body
      )}
    </div>
  );
}
